import { match } from "ts-pattern";

import { defaultNS, useTranslation } from "@/lib/i18n";

type CycleUnit = "DAY" | "WEEK" | "MONTH" | "YEAR";

/**
 * 期間の単位を適切な表示形式に変換するカスタムフック
 *
 * 主な機能:
 * - 期間の単位（日、週、月、年）を言語に応じた適切な表示形式に変換
 * - 単数形/複数形の自動判定と適用
 *
 * 使用例:
 * ```tsx
 * const getCycleUnitText = useGetCycleUnitText();
 * // 日本語の場合: "2ヶ月"
 * // 英語の場合: "2 months"
 * const text = getCycleUnitText("MONTH", 2);
 * ```
 *
 * @returns 期間の単位を変換する関数
 */
export const useGetCycleUnitText = () => {
  const { t } = useTranslation(defaultNS, {
    keyPrefix: "common.date",
  });

  return (cycleUnit: CycleUnit, cycleLength: number) =>
    match([cycleUnit, cycleLength > 1] as const)
      .with(["MONTH", true], () => t("months"))
      .with(["MONTH", false], () => t("month"))
      .with(["YEAR", true], () => t("years"))
      .with(["YEAR", false], () => t("year"))
      .with(["WEEK", true], () => t("weeks"))
      .with(["WEEK", false], () => t("week"))
      .with(["DAY", true], () => t("days"))
      .with(["DAY", false], () => t("day"))
      .exhaustive();
};
